import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, BookOpen, Mail, ListChecks, LogOut, Pencil, ShoppingBag } from "lucide-react";
import { api, getToken, clearToken } from "@/lib/api";
import Logo from "@/components/Logo";

const items = [
  { to: "/admin", icon: LayoutDashboard, label: "Overview", end: true, testid: "admin-nav-overview" },
  { to: "/admin/content", icon: Pencil, label: "Content", testid: "admin-nav-content" },
  { to: "/admin/resources", icon: BookOpen, label: "Resources", testid: "admin-nav-resources" },
  { to: "/admin/enrollments", icon: ShoppingBag, label: "Enrollments", testid: "admin-nav-enrollments" },
  { to: "/admin/contacts", icon: Mail, label: "Contact Inbox", testid: "admin-nav-contacts" },
  { to: "/admin/waitlist", icon: ListChecks, label: "Interest & Waitlist", testid: "admin-nav-waitlist" },
];

export default function AdminLayout() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!getToken()) {
      nav("/admin/login");
      return;
    }
    api
      .get("/auth/me")
      .then((r) => setMe(r.data))
      .catch(() => {
        clearToken();
        nav("/admin/login");
      })
      .finally(() => setChecking(false));
  }, [nav]);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center text-brand-ink/40" data-testid="admin-loading">
        Loading…
      </div>
    );
  }

  const logout = () => {
    clearToken();
    nav("/admin/login");
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex" data-testid="admin-shell">
      <aside className="w-64 shrink-0 border-r border-brand-rule/60 bg-brand-cream p-6 hidden md:flex md:flex-col">
        <Link to="/" className="inline-block" aria-label="Quantum Results">
          <Logo className="h-12 w-auto" />
        </Link>
        <div className="eyebrow mt-8 mb-3">Admin</div>
        <nav className="flex flex-col gap-1">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={it.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2.5 text-[14px] transition-colors ${
                  isActive
                    ? "bg-brand-ink text-brand-cream"
                    : "text-brand-ink/80 hover:bg-brand-paper"
                }`
              }
            >
              <it.icon size={16} />
              {it.label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto pt-6 border-t border-brand-rule/60">
          <div className="text-[12px] text-brand-ink/50 mb-2">{me?.email}</div>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="inline-flex items-center gap-2 text-[13px] text-brand-ink/70 hover:text-brand-blue"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <div className="md:hidden border-b border-brand-rule/60 bg-brand-cream px-4 py-3 flex items-center justify-between">
          <Link to="/" className="inline-block" aria-label="Quantum Results">
            <Logo className="h-10 w-auto" />
          </Link>
          <button onClick={logout} className="text-[13px] text-brand-ink/70">Sign out</button>
        </div>
        <div className="md:hidden border-b border-brand-rule/60 px-4 py-2 flex gap-3 overflow-x-auto">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              className={({ isActive }) =>
                `whitespace-nowrap text-[13px] px-3 py-1.5 rounded-full border ${
                  isActive ? "border-brand-blue text-brand-blue" : "border-brand-rule text-brand-ink/70"
                }`
              }
            >
              {it.label}
            </NavLink>
          ))}
        </div>
        <div className="p-6 sm:p-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
